import { normalizeOutreachCopy } from '../../lib/outreachSequence.js'
import { titleTierEvidence } from '../../lib/titleTierView.js'

// UI consistency only. The server resolves the authenticated sender and owns
// durable reservation, facts, authorization and provider-outcome enforcement.
export function senderSignatureFor(user) {
  const name = normalizeOutreachCopy(user?.name)
  return user?.id && name && !/[\r\n]/.test(String(user.name))
    ? { userId: String(user.id), name } : null
}
export function approvedDossierCopy(variant, signature) {
  if (!signature || !variant?.subject || !variant?.body) return false
  return normalizeOutreachCopy(variant.subject) === variant.subject
    && normalizeOutreachCopy(variant.body) === variant.body
    && variant.body.endsWith(`${signature.name}\nSouthern Recognition`)
}
export function createDossierViewFence() {
  let key, epoch = 0, mounted = true
  return {
    observe(next) { if (next !== key) { key = next; epoch++ } return epoch },
    open() { mounted = true; return epoch },
    current(ticket) { return mounted && ticket === epoch },
    close() { mounted = false; epoch++ },
  }
}
export function unaddressedDraftKey({ championshipId, factRevision, signature, subject, body }) {
  return JSON.stringify([String(championshipId || ''), String(factRevision || ''), signature?.userId, signature?.name, 'unaddressed', subject, body])
}
export function confirmedUnaddressedReceipt(receipt, request, mailbox) {
  return receipt?.ok === true && typeof receipt.draftId === 'string' && receipt.draftId.trim()
    && receipt.sent === false && receipt.recipientStatus === 'needed'
    && receipt.requestId === request.requestId
    && String(receipt.championshipId) === String(request.championshipId)
    && String(receipt.mailbox || '').toLowerCase() === String(mailbox || '').toLowerCase()
    && ['to', 'cc', 'bcc'].every(field => Array.isArray(receipt[field]) && receipt[field].length === 0)
}

export const DOSSIER_DRAFT_FACT_FIELDS = Object.freeze(['championship_name', 'current_champion', 'sr_winning_school', 'sr_sport', 'sr_season_year', 'sr_level', 'governing_body', 'champion_title_line', 'sr_event_date', 'sr_confirmed_date', 'sr_date_status', 'champion_head_coach', 'champion_story', 'champion_story_source'])
export function dossierDraftFacts(raw, championshipId) {
  if (!raw || String(raw.id) !== String(championshipId)
      || !DOSSIER_DRAFT_FACT_FIELDS.every(field => Object.hasOwn(raw, field) && (raw[field] == null || typeof raw[field] === 'string'))) return null
  return Object.fromEntries(DOSSIER_DRAFT_FACT_FIELDS.map(field => [field, String(raw[field] ?? '').trim()]))
}
export function verifiedSequenceDossier(raw, championshipId) {
  const facts = dossierDraftFacts(raw, championshipId)
  if (!facts) return null
  const date = facts.sr_confirmed_date || facts.sr_event_date
  const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00.000Z`) : null
  const safeDate = /^confirmed$/i.test(facts.sr_date_status) && parsedDate && Number.isFinite(parsedDate.getTime()) && parsedDate.toISOString().slice(0, 10) === date
  return {
    championshipId,
    championship: {
      titleTierEvidence: titleTierEvidence(raw),
      sport: facts.sr_sport, season: facts.sr_season_year, level: facts.sr_level,
      dateCustomerSafe: safeDate, effDateLabel: safeDate ? date : '',
      story: { titleLine: facts.champion_title_line || facts.championship_name, text: facts.champion_story },
    },
    champion: { school: facts.sr_winning_school || facts.current_champion, colors: [] },
    coach: { name: facts.champion_head_coach },
  }
}

export const dossierHoldScope = ({userId, mailbox, championshipId}) => JSON.stringify([String(userId || ''), String(mailbox || '').toLowerCase(), String(championshipId || '')])
export function updateDossierHeld(state, scope, update) {
  const current = state[scope] || []
  const next = typeof update === 'function' ? update(current) : update
  return { ...state, [scope]: [...new Set(next)] }
}
export function updateDossierReceipt(state, view, key, status) {
  if (!['addressed', 'recipient_needed', 'demo'].includes(status)) throw new Error('Unknown dossier receipt state')
  return { ...state, [view]: { ...(state[view] || {}), [key]: status } }
}
